import { payoutAPI } from "../api/payout-api";
import {
  PAYOUT_SETTINGS_ERROR,
  PAYMENT_METHODS_ERROR,
  CREATE_PAYOUT_ERROR,
} from "../reducers/types";
import {
  setErrorAC,
  setLoadingAC,
  setPayoutSettingsAC,
  setPaymentMethodsAC,
  updateCurrentAccountAC,
  setWiresListAC,
  setWiresListTotalAC,
} from "./actions";
import { authThunks } from "./auth-thunks";
import { accountThunks } from "./account-thunks";

const handleError = (dispatch, type, error) => {
  if (error.response?.status === 401) {
    dispatch(authThunks.logoutTC());
  }
  dispatch(
    setErrorAC({
      type,
      message: error.response?.data?.message || error.message,
    })
  );
};

export const payoutThunks = {
  /**
   * Loads the wires list. In mock mode (REACT_APP_PAYOUT_MOCK=true) the
   * locally stored pay-out records are merged in, since the real list does
   * not know about them yet.
   */
  loadWiresWithPayoutsTC: (accID, params, options = {}) => async (dispatch, getState) => {
    await dispatch(accountThunks.getWiresListTC(accID, params, options));
    // mock pay-outs are prepended on the first page only, so they are not
    // duplicated or double-counted while loading further pages
    if (!payoutAPI.isMock || options.append) return;
    try {
      const response = await payoutAPI.getPayoutWires(accID);
      const mockPayouts = response.data?.items || [];
      if (!mockPayouts.length) return;
      const { wiresList, wiresListTotal } = getState().accounts;
      const existingIds = new Set((wiresList || []).map((w) => w._id));
      const fresh = mockPayouts.filter((w) => !existingIds.has(w._id));
      dispatch(setWiresListAC([...fresh, ...(wiresList || [])]));
      dispatch(setWiresListTotalAC((wiresListTotal || 0) + fresh.length));
    } catch (e) {
      // mock only
    }
  },

  getPayoutSettingsTC: (accID) => async (dispatch, getState) => {
    try {
      const { currentAccount, extensions } = getState().accounts;
      const response = await payoutAPI.getPayoutSettings(accID, currentAccount, extensions);
      if (response.status === 200 && response.data?.success) {
        dispatch(setPayoutSettingsAC(response.data.settings || null));
      }
      dispatch(setErrorAC({ type: PAYOUT_SETTINGS_ERROR, message: "" }));
    } catch (error) {
      handleError(dispatch, PAYOUT_SETTINGS_ERROR, error);
    }
  },

  updateAutoPayoutTC: (accID, autoPayout) => async (dispatch, getState) => {
    try {
      dispatch(setLoadingAC(true));
      const response = await payoutAPI.updatePayoutSettings(accID, autoPayout);
      if (response.status === 200 && response.data?.success) {
        const { payoutSettings } = getState().accounts;
        dispatch(
          setPayoutSettingsAC({ ...(payoutSettings || {}), ...(response.data.settings || {}) })
        );
        if (response.data.autoPayoutSettings) {
          dispatch(updateCurrentAccountAC({ autoPayoutSettings: response.data.autoPayoutSettings }));
        }
      }
      dispatch(setErrorAC({ type: PAYOUT_SETTINGS_ERROR, message: "" }));
      return response;
    } catch (error) {
      handleError(dispatch, PAYOUT_SETTINGS_ERROR, error);
      throw error;
    } finally {
      dispatch(setLoadingAC(false));
    }
  },

  getPaymentMethodsTC: (accID) => async (dispatch) => {
    try {
      const response = await payoutAPI.getPaymentMethods(accID);
      if (response.status === 200 && response.data?.success) {
        dispatch(setPaymentMethodsAC(response.data.items || {}));
      }
      dispatch(setErrorAC({ type: PAYMENT_METHODS_ERROR, message: "" }));
    } catch (error) {
      handleError(dispatch, PAYMENT_METHODS_ERROR, error);
    }
  },

  savePaymentMethodTC: (accID, kind, data, settingsID) => async (dispatch) => {
    try {
      dispatch(setLoadingAC(true));
      const response = await payoutAPI.savePaymentMethod(accID, kind, data, settingsID);
      if (response.status === 200 && response.data?.success) {
        if (response.data.items) {
          dispatch(setPaymentMethodsAC(response.data.items));
        } else {
          await dispatch(payoutThunks.getPaymentMethodsTC(accID));
        }
      }
      dispatch(setErrorAC({ type: PAYMENT_METHODS_ERROR, message: "" }));
      return response;
    } catch (error) {
      handleError(dispatch, PAYMENT_METHODS_ERROR, error);
      throw error;
    } finally {
      dispatch(setLoadingAC(false));
    }
  },

  deletePaymentMethodTC: (accID, kind) => async (dispatch) => {
    try {
      dispatch(setLoadingAC(true));
      const response = await payoutAPI.deletePaymentMethod(accID, kind);
      if (response.status === 200 && response.data?.success) {
        if (response.data.items) {
          dispatch(setPaymentMethodsAC(response.data.items));
        } else {
          await dispatch(payoutThunks.getPaymentMethodsTC(accID));
        }
        // Auto-payout may reference the removed method
        dispatch(payoutThunks.getPayoutSettingsTC(accID));
      }
      dispatch(setErrorAC({ type: PAYMENT_METHODS_ERROR, message: "" }));
      return response;
    } catch (error) {
      handleError(dispatch, PAYMENT_METHODS_ERROR, error);
      throw error;
    } finally {
      dispatch(setLoadingAC(false));
    }
  },

  createPayoutTC: (accID, body) => async (dispatch, getState) => {
    try {
      dispatch(setLoadingAC(true));
      const response = await payoutAPI.createPayout(accID, body);
      if (response.status === 200 && response.data?.success) {
        if (payoutAPI.isMock && response.data.wire) {
          // Mock mode: the real wires list does not know about this record
          const { wiresList, wiresListTotal } = getState().accounts;
          dispatch(setWiresListAC([response.data.wire, ...(wiresList || [])]));
          dispatch(setWiresListTotalAC((wiresListTotal || 0) + 1));
        } else {
          dispatch(
            accountThunks.getWiresListTC(accID, {
              page: 1,
              pageLen: 25,
              sort: "-createdAt",
            })
          );
        }
        dispatch(payoutThunks.getPayoutSettingsTC(accID));
      }
      dispatch(setErrorAC({ type: CREATE_PAYOUT_ERROR, message: "" }));
      return response;
    } catch (error) {
      handleError(dispatch, CREATE_PAYOUT_ERROR, error);
      throw error;
    } finally {
      dispatch(setLoadingAC(false));
    }
  },
};
